import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /p/:code/event/:city/:slug
 * Captures the promo code (cookie + localStorage), pings track-click,
 * then forwards to the standard EventDetailPage so visitors see the full,
 * polished public event experience identical to the main site.
 */
export default function PartnerLandingPage() {
  const { code, city, slug } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Resolve to validate + get canonical city/slug as fallback
        const res = await axios.get(`${API}/promo/resolve/${code}`);
        const ev = res.data.event;
        // Set 30-day cookie + localStorage so attribution survives across pages
        try {
          const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
          document.cookie = `eventism_promo=${encodeURIComponent(code)}; expires=${expires}; path=/; SameSite=Lax`;
          localStorage.setItem('eventism_promo', JSON.stringify({ code, ts: Date.now() }));
        } catch { /* ignore */ }
        // Fire-and-forget click tracker
        axios.post(`${API}/promo/track-click`, { tracking_code: code }).catch(() => {});
        // Redirect to the canonical event page with ?p= preserved (extra safety)
        const targetCity = city || ev.city;
        const targetSlug = slug || ev.slug;
        if (mounted) navigate(`/events/${targetCity}/${targetSlug}?p=${encodeURIComponent(code)}`, { replace: true });
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || 'Invalid or inactive promo link');
      }
    })();
    return () => { mounted = false; };
  }, [code, city, slug, navigate]);

  if (error) {
    return (
      <div className="min-h-screen pt-20 px-4" data-testid="partner-landing-error">
        <Card className="max-w-md mx-auto text-center">
          <CardContent className="p-8 space-y-3">
            <AlertCircle className="h-10 w-10 text-rose-500 mx-auto" />
            <h1 className="text-xl font-bold">Promo link unavailable</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Link to="/events"><Button>Browse events</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 flex flex-col items-center justify-center" data-testid="partner-landing-loading">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      <p className="text-sm text-muted-foreground mt-3">Taking you to the event…</p>
    </div>
  );
}
